import { useEffect, useRef, useState } from 'react'

import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFormikContext } from 'formik'
import { useIntl } from 'react-intl'

import {
  FormattedMessage,
  Popover,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'

import { ActionFormButton } from '../../ActionForm/ActionFormButton'
import { FormRow } from '../../ActionForm/ui'
import { scrollRowFullyIntoView } from '../../gridRowBand'
import { TooltipLabel } from '../../ui'
import { useIsRowVisible } from '../../useIsRowVisible'
import { CTA_ICONS } from '../auctionCta'
import { BidAmountField } from '../BidAmountField'
import type { useAuctionActionForm } from '../useAuctionActionForm'
import { coverTheCell } from './coverTheCell'
import { CancelButton, OverlayPanel } from './ui'

const ns = 'Listings.ListView.actionCell.auction'
const formNs = 'Listings.ListView.actionPanel.form'
const overlayNs = 'Listings.ListView.actionPanel.overlay'

/**
 * Pop-out bid form for the narrow panel.
 *
 * We use a portal because AG Grid clips cell overflow. This requires the form
 * attribute for the submit button to work.
 *
 * Moves with the row on scroll and hides when the row is not wholly visible.
 */
export const InlineBidOverlay = ({
  anchorElement,
  form,
  formId,
  onDismiss,
  onSubmitted,
}: {
  anchorElement: HTMLElement | null
  form: ReturnType<typeof useAuctionActionForm>
  formId: string
  onDismiss: () => void
  onSubmitted: () => void
}) => {
  const { formatMessage } = useIntl()
  const { isValid, isValidating, submitCount } = useFormikContext()
  const isRowVisible = useIsRowVisible(anchorElement)
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null)
  // The cancel tooltip positions against the button, so it needs the element.
  const [cancelElement, setCancelElement] = useState<HTMLButtonElement | null>(
    null
  )

  const { cta, onClickAction } = form

  /**
   * Closes on a submit that passed validation, rather than on the click that
   * started it: closing from the click handler would detach the CTA before the
   * browser ran the submit it just triggered.
   *
   * The submit is not consumed until validation settles — Formik raises
   * `submitCount` before it has errors to report, so reading the verdict any
   * earlier closes the overlay on a rejected amount. Leaving `lastSubmitCount`
   * alone while `isValidating` is what holds the submit for the render that
   * carries the answer. A rejected amount keeps the overlay open, on the field
   * showing why.
   */
  const lastSubmitCount = useRef(submitCount)
  useEffect(() => {
    if (submitCount === lastSubmitCount.current || isValidating) return
    lastSubmitCount.current = submitCount
    if (isValid) onSubmitted()
  }, [isValid, isValidating, onSubmitted, submitCount])

  /**
   * A row only half in view shows no overlay, so opening one on such a row
   * would look like the gavel did nothing. Bring the row in instead, by the
   * least the grid can scroll.
   *
   * Once, as this opens. The overlay is mounted only while it is open, so the
   * guard is what says that rather than the dependency: the anchor changing
   * identity is not a buyer asking to be moved, and scrolling the grid out from
   * under someone mid-bid is worse than not scrolling at all.
   */
  const hasBroughtRowIntoView = useRef(false)
  useEffect(() => {
    if (hasBroughtRowIntoView.current) return
    hasBroughtRowIntoView.current = true
    scrollRowFullyIntoView(anchorElement)
  }, [anchorElement])

  /**
   * The overlay opens for the sole purpose of entering an amount, so the amount
   * takes focus.
   *
   * Focused here rather than with `autoFocus`, and with `preventScroll`,
   * because the attribute takes the page with it: the overlay is portalled to
   * the document and mounts at its top-left corner, before floating-ui has
   * placed it against the row. The browser scrolls whatever it just focused
   * into view, so focusing it there jumps the page to the top.
   */
  useEffect(() => {
    // The overlay holds exactly one input — the amount. Anything added beside
    // it needs a ref here rather than this query.
    panelElement?.querySelector('input')?.focus({ preventScroll: true })
  }, [panelElement])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [onDismiss])

  const ctaLabel = formatMessage({ id: `${ns}.cta.${cta}` })
  const cancelLabel = formatMessage({ id: `${overlayNs}.cancel.label` })

  return (
    <Popover
      open
      options={{
        placement: 'bottom-end',
        middleware: [coverTheCell],
      }}
      referenceElement={anchorElement}
      setOpen={(open) => {
        if (!open) onDismiss()
      }}
    >
      <OverlayPanel $isRowVisible={isRowVisible} ref={setPanelElement}>
        <FormRow>
          <BidAmountField form={form} />
          <ActionFormButton
            description={formatMessage({
              id: `${formNs}.description.${cta}`,
            })}
            form={formId}
            icon={CTA_ICONS[cta]}
            iconOnly={false}
            label={ctaLabel}
            onClick={onClickAction}
          />
        </FormRow>

        <CancelButton
          aria-label={cancelLabel}
          onClick={onDismiss}
          ref={setCancelElement}
          type="button"
        >
          <FontAwesomeIcon icon={faXmark} />
        </CancelButton>
        <Tooltip referenceElement={cancelElement}>
          <TooltipLabel>{cancelLabel}</TooltipLabel>
          <FormattedMessage id={`${overlayNs}.cancel.description`} />
        </Tooltip>
      </OverlayPanel>
    </Popover>
  )
}
